/**
 * Mobile Menu Module
 * Slide-out menu from left with hover background images
 */
class MobileMenu {
  constructor() {
    this.isActive = false;
    this.toggleButton = document.querySelector(".mobile-menu-toggle");
    this.overlay = document.querySelector(".mobile-menu-overlay");
    this.menu = document.querySelector("#mobile-menu");
    this.bgLayers = this.menu
      ? this.menu.querySelectorAll(".mobile-bg-layer")
      : [];
    this.linksWithBg = this.menu
      ? this.menu.querySelectorAll(".mobile-link[data-hover-bg]")
      : [];
    this.allLinks = this.menu ? this.menu.querySelectorAll(".mobile-link") : [];
    this.pendingBgIndex = -1;
    this.hoverBgsPrefetched = false;

    this.init();
  }

  init() {
    if (!this.toggleButton || !this.overlay || !this.menu) {
      // if (this.isDebugEnabled()) {
      //   console.warn("[MOBILE MENU] Mobile menu elements not found");
      // }
      return;
    }

    if (this.menu) {
      this.menu.setAttribute("aria-hidden", "true");
      this.menu.removeAttribute("tabindex");
      this.menu.classList.remove("is-open");
    }

    this.bindEvents();
    this.bindHoverBackgrounds();
    this.bindSubmenus();

    // if (this.isDebugEnabled()) {
    //   console.log("[MOBILE MENU] Mobile menu initialized");
    // }
  }

  isDebugEnabled() {
    return typeof sojTheme !== "undefined" && sojTheme.debug;
  }

  bindEvents() {
    this.toggleButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggle();
    });

    this.overlay.addEventListener("click", (e) => {
      e.preventDefault();
      this.close();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isActive) {
        this.close();
      }
    });
  }

  bindSubmenus() {
    if (!this.menu) return;

    this.menu.querySelectorAll(".mobile-submenu-toggle").forEach((toggle) => {
      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleSubmenu(toggle);
      });
    });
  }

  toggleSubmenu(toggle) {
    const item = toggle.closest(".mobile-link-item");
    if (!item) return;

    const wasOpen = item.classList.contains("is-open");

    this.menu.querySelectorAll(".mobile-link-item.is-open").forEach((other) => {
      if (other !== item) {
        this.closeSubmenu(other);
      }
    });
    this.clearActiveStates();

    if (wasOpen) {
      this.closeSubmenu(item);
    } else {
      this.openSubmenu(item);
    }
  }

  openSubmenu(item) {
    const toggle = item.querySelector(".mobile-submenu-toggle");
    const submenu = item.querySelector(".mobile-submenu");
    item.classList.add("is-open");
    if (toggle) {
      toggle.setAttribute("aria-expanded", "true");
      const hideLabel = toggle.getAttribute("data-label-hide");
      if (hideLabel) {
        toggle.setAttribute("aria-label", hideLabel);
      }
    }
    if (submenu) {
      submenu.setAttribute("aria-hidden", "false");
      submenu.removeAttribute("inert");
    }
  }

  closeSubmenu(item) {
    const toggle = item.querySelector(".mobile-submenu-toggle");
    const submenu = item.querySelector(".mobile-submenu");
    item.classList.remove("is-open");
    if (toggle) {
      toggle.setAttribute("aria-expanded", "false");
      const showLabel = toggle.getAttribute("data-label-show");
      if (showLabel) {
        toggle.setAttribute("aria-label", showLabel);
      }
    }
    if (submenu) {
      submenu.setAttribute("aria-hidden", "true");
      submenu.setAttribute("inert", "");
    }
  }

  closeAllSubmenus() {
    if (!this.menu) return;
    this.menu.querySelectorAll(".mobile-link-item.is-open").forEach((item) => {
      this.closeSubmenu(item);
    });
    this.clearActiveStates();
  }

  clearActiveStates() {
    if (!this.menu) return;
    this.menu.querySelectorAll(".mobile-link-item").forEach((item) => {
      item.classList.remove("is-active");
      item.classList.remove("is-dimmed");
    });
    this.pendingBgIndex = -1;
    this.bgLayers.forEach((layer) => layer.classList.remove("active"));
  }

  applyLayerBackground(layer) {
    if (!layer || layer.dataset.bgApplied === "1") {
      return Promise.resolve();
    }

    const url = layer.getAttribute("data-bg");
    if (!url) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const img = new Image();
      const finish = () => {
        layer.style.backgroundImage = `url(${JSON.stringify(url)})`;
        layer.dataset.bgApplied = "1";
        resolve();
      };
      img.onload = finish;
      img.onerror = finish;
      img.src = url;
    });
  }

  prefetchHoverBackgrounds() {
    if (this.hoverBgsPrefetched || !this.bgLayers.length) {
      return;
    }

    if (
      typeof window.matchMedia === "function" &&
      !window.matchMedia("(hover: hover) and (pointer: fine)").matches
    ) {
      return;
    }

    this.hoverBgsPrefetched = true;

    const run = () => {
      this.bgLayers.forEach((layer) => {
        this.applyLayerBackground(layer);
      });
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(run, { timeout: 2500 });
      return;
    }

    window.setTimeout(run, 500);
  }

  bindHoverBackgrounds() {
    if (!this.menu || !this.bgLayers.length) return;

    const setActive = (index) => {
      this.pendingBgIndex = index;

      if (index < 0) {
        this.bgLayers.forEach((layer) => layer.classList.remove("active"));
        return;
      }

      const layer = this.bgLayers[index];
      if (!layer) {
        return;
      }

      this.applyLayerBackground(layer).then(() => {
        if (this.pendingBgIndex !== index) {
          return;
        }

        // Paint the image at opacity 0 first, then add .active so the CSS fade runs.
        requestAnimationFrame(() => {
          if (this.pendingBgIndex !== index) {
            return;
          }

          requestAnimationFrame(() => {
            if (this.pendingBgIndex !== index) {
              return;
            }

            this.bgLayers.forEach((item, i) => {
              item.classList.toggle("active", i === index);
            });
          });
        });
      });
    };

    const items = this.menu.querySelectorAll(".mobile-link-item");

    const setHoveredItem = (item) => {
      items.forEach((other) => {
        const isActive = other === item;
        other.classList.toggle("is-active", isActive);
        other.classList.toggle("is-dimmed", !isActive);
      });

      const link = item.querySelector(".mobile-link");
      if (link && link.hasAttribute("data-hover-bg")) {
        const idx = Array.from(this.linksWithBg).indexOf(link);
        if (idx >= 0) setActive(idx);
      } else {
        setActive(-1);
      }
    };

    const clearHover = () => {
      this.clearActiveStates();
    };

    items.forEach((item) => {
      const row = item.querySelector(".mobile-link-row");
      if (!row) return;

      row.addEventListener("mouseenter", () => setHoveredItem(item));

      // When leaving a row to anywhere that is not another row,
      // clear all dimming so every item returns to full white.
      row.addEventListener("mouseleave", (event) => {
        const nextTarget = event.relatedTarget;
        if (
          nextTarget &&
          typeof nextTarget.closest === "function" &&
          nextTarget.closest(".mobile-link-row")
        ) {
          // Moving directly to another row – let its mouseenter handler take over.
          return;
        }

        clearHover();
      });
    });

    const content = this.menu.querySelector(".mobile-content");
    if (content) {
      content.addEventListener("mouseleave", () => {
        setActive(-1);
        clearHover();
      });
    }
  }

  updateToggleText() {
    if (!this.toggleButton) return;

    const textEl = this.toggleButton.querySelector(".text");
    const menuText = this.toggleButton.getAttribute("data-menu-text") || "Menu";
    const closeText =
      this.toggleButton.getAttribute("data-close-text") || "Close";

    if (textEl) {
      textEl.textContent = this.isActive ? closeText : menuText;
    }
    this.toggleButton.setAttribute(
      "aria-expanded",
      this.isActive ? "true" : "false",
    );
  }

  toggle() {
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // Close consultation popout if open (only one panel at a time)
    const consultationPopout =
      typeof window.SOJ !== "undefined" && window.SOJ.getConsultationPopout();
    if (consultationPopout && consultationPopout.isActive) {
      consultationPopout.close();
    }

    // Save viewport scroll before drawer changes layout / overflow on `<html>`
    const scrollY =
      typeof window.SOJ_SMOOTH_SCROLL !== "undefined" &&
      window.SOJ_SMOOTH_SCROLL.getScrollPosition
        ? window.SOJ_SMOOTH_SCROLL.getScrollPosition()
        : (window.scrollY ?? document.documentElement.scrollTop);

    // Drawer open shifts overflow on `<html>` — SOJ_SMOOTH_SCROLL.scrollTo mirrors native restore below
    document.dispatchEvent(new CustomEvent("mobileMenuOpen"));

    document.documentElement.classList.add("mobile-menu-active");
    if (this.menu) {
      this.menu.classList.add("is-open");
      this.menu.setAttribute("tabindex", "-1");
      this.menu.setAttribute("aria-hidden", "false");
    }
    this.isActive = true;
    this.updateToggleText();
    this.prefetchHoverBackgrounds();

    // Preserve scroll across overflow / modal layout changes on the `<html>` root
    const restore = () => {
      if (
        typeof window.SOJ_SMOOTH_SCROLL !== "undefined" &&
        window.SOJ_SMOOTH_SCROLL.scrollTo
      ) {
        window.SOJ_SMOOTH_SCROLL.scrollTo(scrollY, {
          immediate: true,
          force: true,
        });
      }
      window.scrollTo(0, scrollY);
    };
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });

    // if (this.isDebugEnabled()) {
    //   console.log("[MOBILE MENU] Mobile menu opened");
    // }
  }

  close() {
    document.documentElement.classList.remove("mobile-menu-active");
    if (this.menu) {
      this.menu.classList.remove("is-open");
      this.menu.removeAttribute("tabindex");
      this.menu.setAttribute("aria-hidden", "true");
      this.bgLayers.forEach((layer) => layer.classList.remove("active"));
      this.closeAllSubmenus();
      if (
        this.menu === document.activeElement ||
        this.menu.contains(document.activeElement)
      ) {
        if (this.toggleButton) {
          this.toggleButton.focus();
        } else {
          document.body.focus();
        }
      }
    }
    this.isActive = false;
    this.updateToggleText();

    document.dispatchEvent(new CustomEvent("mobileMenuClose"));

    // if (this.isDebugEnabled()) {
    //   console.log("[MOBILE MENU] Mobile menu closed");
    // }
  }
}

export default MobileMenu;
